import { useState, useEffect, useCallback } from 'react';
import { getSharedViewsState, saveSharedViewsState } from 'services/api/ClientHandler';
import { convertUrlToDynamic } from 'services/helpers/dateParser';

/**
 * Hook pour gérer les vues partagées au niveau organisation
 * Utilise le backend (config/options) pour synchroniser entre utilisateurs
 * Supporte les dates dynamiques (@today, -Xd)
 */
const useSharedViews = (user) => {
    const [sharedViews, setSharedViews] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Déterminer les permissions (admin ou account-manager peuvent éditer)
    const canEdit = user?.roles?.includes('mobiuspack-admin') || user?.roles?.includes('account-manager') || false;

    // Charger les vues depuis le backend au mount
    useEffect(() => {
        if (!user?.id || !user?.token) return;

        const loadSharedViews = async () => {
            setIsLoading(true);
            try {
                const response = await getSharedViewsState(user.id, user.token);
                if (response.status === 200 && Array.isArray(response.results)) {
                    setSharedViews(response.results);
                }
            } catch (error) {
                console.error('[useSharedViews] Error loading:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadSharedViews();
    }, [user?.id, user?.token]);

    // Ajouter une nouvelle vue partagée
    const addSharedView = useCallback(async (label, forceOverwrite = false) => {
        if (!canEdit) {
            console.warn('[useSharedViews] User does not have edit permissions');
            return { success: false, message: 'Permission refusée' };
        }

        const relativeUrl = window.location.pathname + window.location.search;
        
        // Convertir les dates en format dynamique si applicable
        const { url: processedUrl, isDynamic } = convertUrlToDynamic(relativeUrl);

        // Créer l'objet vue avec métadonnées
        const newView = {
            path: processedUrl,
            label,
            isDynamic,
            createdAt: new Date().toISOString(),
            createdBy: user.email,
            createdByName: user.name || user.displayName || user.email,
        };

        // Vérifier si une vue avec le même label existe déjà
        const existingWithSameLabel = sharedViews.find(v => v.label === label);
        if (existingWithSameLabel && !forceOverwrite) {
            return {
                success: false,
                conflict: true,
                existingView: existingWithSameLabel,
                message: `Une vue partagée "${label}" existe déjà`,
            };
        }

        // Dédupliquer par path (remplacer si existe déjà)
        const updated = [...sharedViews.filter(v => v.path !== newView.path), newView];

        // Mise à jour optimiste
        setSharedViews(updated);

        // Sauvegarder dans le backend
        setIsSaving(true);
        try {
            const response = await saveSharedViewsState(user.id, user.token, updated);
            setIsSaving(false);

            if (response.status === 200) {
                return { 
                    success: true, 
                    message: isDynamic 
                        ? 'Vue partagée ajoutée (dates dynamiques)' 
                        : 'Vue partagée ajoutée (dates fixes)',
                    isDynamic
                };
            } else {
                // Rollback en cas d'échec
                setSharedViews(sharedViews);
                return { success: false, message: 'Erreur lors de la sauvegarde' };
            }
        } catch (error) {
            console.error('[useSharedViews] Error saving:', error);
            setSharedViews(sharedViews); // Rollback
            setIsSaving(false);
            return { success: false, message: error.message };
        }
    }, [sharedViews, canEdit, user]);

    // Supprimer une vue partagée
    const removeSharedView = useCallback(async (path) => {
        if (!canEdit) {
            console.warn('[useSharedViews] User does not have edit permissions');
            return { success: false, message: 'Permission refusée' };
        }

        const updated = sharedViews.filter(v => v.path !== path);

        // Mise à jour optimiste
        setSharedViews(updated);

        // Sauvegarder dans le backend
        setIsSaving(true);
        try {
            const response = await saveSharedViewsState(user.id, user.token, updated);
            setIsSaving(false);

            if (response.status === 200) {
                return { success: true, message: 'Vue partagée supprimée' };
            } else {
                // Rollback en cas d'échec
                setSharedViews(sharedViews);
                return { success: false, message: 'Erreur lors de la suppression' };
            }
        } catch (error) {
            console.error('[useSharedViews] Error removing:', error);
            setSharedViews(sharedViews); // Rollback
            setIsSaving(false);
            return { success: false, message: error.message };
        }
    }, [sharedViews, canEdit, user]);

    // Modifier le label d'une vue partagée
    const updateSharedViewLabel = useCallback(async (path, newLabel) => {
        if (!canEdit) {
            console.warn('[useSharedViews] User does not have edit permissions');
            return { success: false, message: 'Permission refusée' };
        }

        const updated = sharedViews.map(v =>
            v.path === path ? { ...v, label: newLabel } : v
        );

        // Mise à jour optimiste
        setSharedViews(updated);

        // Sauvegarder dans le backend
        setIsSaving(true);
        try {
            const response = await saveSharedViewsState(user.id, user.token, updated);
            setIsSaving(false);

            if (response.status === 200) {
                return { success: true, message: 'Label modifié' };
            } else {
                // Rollback en cas d'échec
                setSharedViews(sharedViews);
                return { success: false, message: 'Erreur lors de la modification' };
            }
        } catch (error) {
            console.error('[useSharedViews] Error updating:', error);
            setSharedViews(sharedViews); // Rollback
            setIsSaving(false);
            return { success: false, message: error.message };
        }
    }, [sharedViews, canEdit, user]);

    return {
        sharedViews,
        addSharedView,
        removeSharedView,
        updateSharedViewLabel,
        canEdit,
        isLoading,
        isSaving
    };
};

export { useSharedViews };
